"use client";

// ── The newest answered clarification, on the page the contestant is on ─────
// `EX-ANN02`. A contestant who asked waits in the workspace, and a broadcast
// answer matters to everybody coding, but answers were read only on the
// contest page's Clarifications tab. This shows the newest answer this browser
// has not acknowledged: whose question it was (yours, or one answered for
// everybody), the problem, the question, and the answer.
// Nothing here is fetched: `useClarificationInbox` owns the poll and the marker.

import { useTranslations } from "next-intl";

import type { ContestClarification } from "../types";
import { ContestNoticeAlert } from "./ContestNoticeAlert";

export function ContestClarificationAlert({
  unseen,
  onSeen,
  readAllHref,
  onReadAll,
}: {
  /** Newest answer first, from `useClarificationInbox`. */
  unseen: readonly ContestClarification[];
  onSeen: () => void;
  readAllHref?: string;
  onReadAll?: () => void;
}) {
  const t = useTranslations("contest.clarifications");
  const newest = unseen[0];
  if (!newest?.answer) return null;
  const more = unseen.length - 1;
  const about = newest.letter ? t("aboutProblem", { letter: newest.letter }) : t("aboutContest");
  return (
    <ContestNoticeAlert
      testId="contest-clarification-alert"
      label={`${newest.mine ? t("alertMine") : t("alertEveryone")} · ${about}`}
      context={newest.question}
      body={newest.answer}
      more={more > 0 ? t("alertMore", { count: more }) : null}
      readAllLabel={t("alertReadAll")}
      readAllHref={readAllHref}
      onReadAll={onReadAll}
      gotItLabel={t("alertGotIt")}
      onSeen={onSeen}
    />
  );
}
